import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent, HttpRequest } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  DocumentApiEntry,
  DocumentDownloadResponse,
  DocumentEntry,
  DocumentRepositoryConfirmRequest,
  DocumentRepositoryConfirmResponse,
  DocumentRepositoryPresignedResponse,
  DocumentRepositoryPresignedTarget,
  DocumentStatus,
  ContactsApiResponse,
} from '../models/document-entry.model';
import { SKIP_INTERCEPTOR } from '../../legal-entity-list/services/legal-entity.service';
import config from '../../../../config.js';

const STATUS_MAP: Record<string, DocumentStatus> = {
  completed: 'complete',
  complete: 'complete',
  pending: 'pending',
  incomplete: 'incomplete',
  deactivated: 'deactivated',
};

@Injectable({ providedIn: 'root' })
export class DocumentRepositoryService {
  private readonly base = config.API_GATEWAY_ENDPOINT?.replace(/\/$/, '') || '';
  private readonly endpoint = `${this.base}/document_repository`;

  constructor(private http: HttpClient) {}

  getDocuments(): Observable<DocumentEntry[]> {
    return this.http.get<DocumentApiEntry[]>(this.endpoint).pipe(
      map((entries) =>
        entries.map((e) => ({
          id: e.id,
          name: e.document,
          uploadDate: e.upload_date,
          status: STATUS_MAP[e.status.toLowerCase()] ?? (e.status.toLowerCase() as DocumentStatus),
          uploadedBy: e.uploaded_by ?? '',
        })),
      ),
    );
  }

  getDownloadUrl(documentType: string): Observable<DocumentDownloadResponse> {
    return this.http.get<DocumentDownloadResponse>(
      `${this.endpoint}/download?document_type=${encodeURIComponent(documentType)}`,
    );
  }

  getPresignedUrl(): Observable<DocumentRepositoryPresignedResponse> {
    return this.http.get<DocumentRepositoryPresignedResponse>(`${this.endpoint}/presigned-url`);
  }

  uploadToS3(
    file: File,
    presignedPost: DocumentRepositoryPresignedTarget['presigned_post'],
  ): Observable<HttpEvent<unknown>> {
    const formData = new FormData();
    Object.entries(presignedPost.fields).forEach(([key, value]) => {
      formData.append(key, value);
    });
    formData.append('file', file, file.name);
    const req = new HttpRequest('POST', presignedPost.url, formData, {
      reportProgress: true,
      context: new HttpContext().set(SKIP_INTERCEPTOR, true),
    });
    return this.http.request(req);
  }

  confirmUpload(
    body: DocumentRepositoryConfirmRequest,
  ): Observable<DocumentRepositoryConfirmResponse> {
    const requestBody =
      body.new_document_name === undefined
        ? { document_type: body.document_type, file_s3_key: body.file_s3_key }
        : { ...body, new_document_name: body.new_document_name.trim() };
    return this.http.post<DocumentRepositoryConfirmResponse>(
      `${this.endpoint}/confirm-upload`,
      requestBody,
    );
  }

  getContacts(documentType: string): Observable<ContactsApiResponse> {
    return this.http.get<ContactsApiResponse>(
      `${this.endpoint}/contacts?document_type=${encodeURIComponent(documentType)}`,
    );
  }
}
